import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Loads `<script>` tags at runtime, browser-only, deduplicating by URL.
 *
 * Used by widgets to lazy-load third-party SDKs (e.g. Sketchfab) and
 * agency-delivered `main.js` files that aren't part of the Angular bundle.
 *
 * The per-URL `Promise` is cached for the lifetime of the document, so
 * multiple widgets (or repeated widget mounts during SPA navigation) trigger
 * the network request at most once.
 */
@Injectable({ providedIn: 'root' })
export class ScriptLoaderService {
    private readonly _document = inject(DOCUMENT);
    private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    private readonly _cache = new Map<string, Promise<void>>();

    load(url: string): Promise<void> {
        if (!this._isBrowser) {
            return Promise.resolve();
        }

        const cached = this._cache.get(url);
        if (cached) {
            return cached;
        }

        const promise = new Promise<void>((resolve, reject) => {
            const script = this._document.createElement('script');
            script.src = url;
            script.async = false;
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () => reject(new Error(`Failed to load script: ${url}`)));
            this._document.head.appendChild(script);
        });

        this._cache.set(url, promise);
        return promise;
    }
}
